import { Form, FormInstance, Select, Switch } from "antd";
import { DiffRow, fmtBool } from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { AutoCompleteScope } from "shared/types/enum";

const KEY = "auto-complete";

type Values = {
  auto_complete_bookings: boolean;
  auto_complete_scope: AutoCompleteScope;
};

// Dropdown order is the product owner's: every class, private only, open only.
const SCOPE_ORDER = [AutoCompleteScope.All, AutoCompleteScope.Private, AutoCompleteScope.Open];

const scopeLabel = (scope: AutoCompleteScope) =>
  translate(`class_settings.auto_complete.scope_${scope}`);

// A switch, plus a class-type scope row while it is on. The gate itself lives
// in autoCompleteFinishedBookings (server booking_v2.ts): turning it back ON
// (or widening the scope) sweeps every ended Confirmed booking on the next
// 5-minute run, except the ones an admin undid back to Confirmed after class.
const Fields = ({ form }: { form: FormInstance }) => {
  const enabled = Form.useWatch("auto_complete_bookings", form);
  const toggleLabel = translate("class_settings.auto_complete.toggle");
  const scopeFieldLabel = translate("class_settings.auto_complete.scope");
  // Built at render time: translate() is not reactive at module scope.
  const scopeOptions = SCOPE_ORDER.map((value) => ({ value, label: scopeLabel(value) }));

  return (
    <SettingsSection
      id={KEY}
      title={translate("class_settings.auto_complete.section")}
      description={translate("settings_nav.desc.auto_complete")}
    >
      <SettingRow
        last={!enabled}
        title={toggleLabel}
        control={
          <Form.Item name="auto_complete_bookings" valuePropName="checked" noStyle>
            <Switch aria-label={toggleLabel} />
          </Form.Item>
        }
      />
      {/* Unmounts while off; AntD keeps the value in the form store, so
          switching back on brings the last choice back. */}
      {enabled && (
        <SettingRow
          last
          title={scopeFieldLabel}
          control={
            <Form.Item name="auto_complete_scope" noStyle>
              <Select aria-label={scopeFieldLabel} options={scopeOptions} style={{ width: 200 }} />
            </Form.Item>
          }
        />
      )}
    </SettingsSection>
  );
};

export const autoCompletePolicy: PolicySection = {
  key: KEY,
  initialValues: (s) => ({
    auto_complete_bookings: s.auto_complete_bookings,
    auto_complete_scope: s.auto_complete_scope,
  }),
  buildSave: (v: Values, s) => {
    const isEnabled = !!v.auto_complete_bookings;
    const nextScope = v.auto_complete_scope ?? s.auto_complete_scope;
    const rows: DiffRow[] = [];
    if (isEnabled !== s.auto_complete_bookings) {
      rows.push({
        label: translate("class_settings.auto_complete.toggle"),
        before: fmtBool(s.auto_complete_bookings),
        after: fmtBool(isEnabled),
      });
    }
    // The scope only matters while the sweep is on: a change made while off
    // is neither listed nor sent, and the stored value is kept.
    if (isEnabled && nextScope !== s.auto_complete_scope) {
      rows.push({
        label: translate("class_settings.auto_complete.scope"),
        before: scopeLabel(s.auto_complete_scope),
        after: scopeLabel(nextScope),
      });
    }
    // The note describes the state the save produces: off, or on for every
    // class / open classes only / private classes only.
    const note = translate(
      !isEnabled
        ? "class_settings.confirm.note_auto_complete_off"
        : nextScope === AutoCompleteScope.Open
        ? "class_settings.confirm.note_auto_complete_on_open"
        : nextScope === AutoCompleteScope.Private
        ? "class_settings.confirm.note_auto_complete_on_private"
        : "class_settings.confirm.note_auto_complete_on",
    );
    return {
      rows,
      payload: {
        auto_complete_bookings: isEnabled,
        ...(isEnabled ? { auto_complete_scope: nextScope } : {}),
      },
      note,
    };
  },
  Fields,
};
